import {
  ActionIcon,
  Box,
  Grid,
  Group,
  Menu,
  Paper,
  Stack,
  Text,
} from "@mantine/core";
import {
  IconClipboardText,
  IconDotsVertical,
  IconDownload,
  IconEdit,
  IconEye,
  IconFile,
  IconFileText,
  IconFileTypeDoc,
  IconFileTypePdf,
  IconFolder,
  IconFolderSymlink,
  IconMusic,
  IconNote,
  IconPhoto,
  IconTrash,
  IconVideo,
} from "@tabler/icons-react";
import React from "react";
import type { FileOutput, FolderOutput } from "@/api-types";
import { TrainerRoutes, UniversalRoutes, useNavigator } from "@/routers/routes";

// Types are now imported directly from @/api-types

type FileProps = {
  kind: "file";
  item: FileOutput;
  onDelete?: (id: string) => void;
  readonly?: boolean;
  onFileClick?: (item: FileOutput) => void;
  children?: React.ReactNode;
};

type FolderProps = {
  kind: "folder";
  item: FolderOutput;
  onDelete?: (id: string) => void;
  onFolderClick: (id: string) => void;
  readonly?: boolean;
  children?: React.ReactNode;
};

type LibraryItemProps<T extends FileOutput | FolderOutput> =
  T extends FileOutput ? FileProps : Omit<FolderProps, "onFileClick">;

type ActionItem = {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  color?: string;
  isReadonly?: boolean;
};

function getFileIcon(item: FileOutput | FolderOutput): typeof IconFile {
  const fileType = (item as FileOutput).type;
  switch (fileType) {
    case "PDF":
      return IconFileTypePdf;
    case "DOCUMENT":
      return IconFileTypeDoc;
    case "TEXT":
      return IconFileText;
    case "IMAGE":
      return IconPhoto;
    case "AUDIO":
      return IconMusic;
    case "VIDEO":
      return IconVideo;
    case "NOTE":
      return IconNote;
    case "SURVEY":
      return IconClipboardText;
    default:
      return IconFile;
  }
}

const getFileActions = (
  file: FileOutput,
  navigator: ReturnType<typeof useNavigator>["navigator"],
): ActionItem[] =>
  [
    {
      label: "Open",
      icon: <IconEye />,
      onClick: navigator({
        path: UniversalRoutes.FILE_PREVIEW,
        params: { fileId: file.id },
      }),
    },
    file.downloadUrl && {
      label: "Download",
      icon: <IconDownload />,
      onClick: () =>
        file.downloadUrl && window.open(file.downloadUrl, "_blank"),
    },
    {
      label: "Edit",
      icon: <IconEdit />,
      onClick: navigator({
        path: TrainerRoutes.LIBRARY.FILE_EDIT,
        params: { fileId: file.id },
      }),
      isReadonly: true,
    },
    {
      label: "Move to folder",
      icon: <IconFolderSymlink />,
      onClick: navigator({
        path: TrainerRoutes.LIBRARY.MOVE_TO_FOLDER,
        params: {
          itemId: file.id,
          itemType: "file",
        },
      }),
      isReadonly: true,
    },
  ].filter(Boolean) as ActionItem[];

const getFolderActions = (
  folder: FolderOutput,
  navigator: ReturnType<typeof useNavigator>["navigator"],
): ActionItem[] => [
  {
    label: "Edit",
    icon: <IconEdit />,
    onClick: navigator({
      path: TrainerRoutes.LIBRARY.FOLDER_EDIT,
      params: { folderId: folder.id },
    }),
    isReadonly: true,
  },
  {
    label: "Move to folder",
    icon: <IconFolderSymlink />,
    onClick: navigator({
      path: TrainerRoutes.LIBRARY.MOVE_TO_FOLDER,
      params: {
        itemId: folder.id,
        itemType: "folder",
      },
    }),
    isReadonly: true,
  },
];

export function LibraryItem<T extends FileOutput | FolderOutput>(
  props: LibraryItemProps<T>,
) {
  const { item, onDelete, readonly, children } = props;
  const { navigator, navigate } = useNavigator();

  const handleClick = () => {
    if (props.kind === "folder") {
      props.onFolderClick((item as FolderOutput).id);
      return;
    }

    // At this point TypeScript knows it's a file
    const fileItem = item as FileOutput;
    const fileProps = props as FileProps;

    if (fileProps.onFileClick == null) {
      return;
    }

    if (fileProps.onFileClick) {
      fileProps.onFileClick(fileItem);
      return;
    }

    navigate({
      path: UniversalRoutes.FILE_PREVIEW,
      params: { fileId: fileItem.id },
    });
  };

  const actions: ActionItem[] = (
    props.kind === "file"
      ? getFileActions(item as FileOutput, navigator)
      : getFolderActions(item as FolderOutput, navigator)
  ).filter((action) => !(readonly && action.isReadonly));

  const icon = props.kind === "file" ? getFileIcon(item) : IconFolder;

  return (
    <Paper p="lg" radius="md" withBorder>
      <Grid align="center">
        <Grid.Col
          onClick={handleClick}
          span="content"
          style={{ display: "flex", alignItems: "center" }}
        >
          {React.createElement(icon, {})}
        </Grid.Col>
        <Grid.Col
          onClick={handleClick}
          span="auto"
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Stack gap="0">
            <Text size="lg">{item.name}</Text>
            {item.description && (
              <Text size="sm" c="dimmed">
                {item.description}
              </Text>
            )}
            {children && <Box mt="xs">{children}</Box>}
          </Stack>
        </Grid.Col>
        {(actions.length > 0 || (onDelete && !readonly)) && (
          <Grid.Col
            span="content"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <Group justify="flex-end">
              <Menu>
                <Menu.Target>
                  <ActionIcon variant="light" size="lg">
                    <IconDotsVertical />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  {actions.map((action, i) => (
                    <Menu.Item
                      key={i}
                      leftSection={action.icon}
                      onClick={action.onClick}
                      color={action.color}
                    >
                      {action.label}
                    </Menu.Item>
                  ))}
                  {onDelete && (
                    <Menu.Item
                      color="red"
                      leftSection={<IconTrash />}
                      onClick={() => onDelete(item.id)}
                    >
                      Delete
                    </Menu.Item>
                  )}
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Grid.Col>
        )}
      </Grid>
    </Paper>
  );
}
